import { readJson, writeRaw } from './storage';

/**
 * The settings, which survive a restart.
 *
 * They live under their own storage key, separate from the save, and that split
 * is the decision rather than a filing convenience (D-124): *restart completely*
 * throws away progress and keeps settings. A player who turned Gentle Mode on
 * did not ask to have it turned off by starting over.
 *
 * ── Why the mute flag is NOT in here ────────────────────────────────────────
 *
 * `rosalie:muted` predates this module and is read during audio construction in
 * `main.ts`, before anything else exists. Moving it would be a change to the
 * boot path of a subsystem this session is not otherwise touching, in a session
 * that is already introducing the game's first persistence — and the failure it
 * would risk (audio that comes up unmuted for somebody who muted it) is one
 * nobody would notice in a test. It stays where it is. The pause menu reads and
 * writes it through the handle it already has.
 */

const SETTINGS_KEY = 'rosalie:settings';

export interface Settings {
  /**
   * Gentle Mode. Spec §2: *"lengthens cutaways and mutes the worst audio."*
   *
   * **Default off, and offered at the content notice** (D-123) — the one moment
   * it is in context, and the last moment before beat 1's taking, which is the
   * first thing it changes.
   */
  readonly gentle: boolean;
  /**
   * Whether the content notice has been read and dismissed at least once.
   *
   * The notice is shown on every cold open regardless; this exists so the
   * *Gentle Mode question* is asked once rather than every single time. Spec §2
   * requires the notice, not an interrogation.
   */
  readonly noticeAcknowledged: boolean;
}

const DEFAULTS: Settings = { gentle: false, noticeAcknowledged: false };

/**
 * The live value, cached.
 *
 * `gentleMode()` is called from `AudioEngine.play()`, which runs on every sound
 * the game starts, and a `localStorage.getItem` plus a `JSON.parse` per one-shot
 * is a synchronous disk touch in the audio path. It is read once and updated on
 * write; nothing else in the process can change it.
 */
let cached: Settings | null = null;

export function readSettings(): Settings {
  if (cached) return cached;
  const stored = readJson(SETTINGS_KEY);
  cached = stored
    ? {
        gentle: stored['gentle'] === true,
        noticeAcknowledged: stored['noticeAcknowledged'] === true,
      }
    : DEFAULTS;
  return cached;
}

/** Merge a change in, persist it, and return the result. */
export function writeSettings(patch: Partial<Settings>): Settings {
  const next: Settings = { ...readSettings(), ...patch };
  cached = next;
  writeRaw(SETTINGS_KEY, JSON.stringify(next));
  return next;
}

/**
 * Is Gentle Mode on? The hot accessor, safe to call per sound and per frame.
 */
export function gentleMode(): boolean {
  return readSettings().gentle;
}

/**
 * Drop the cache. **For tests only**, which drive several settings states inside
 * one page, and for nothing in the game — where the cache is written through.
 */
export function resetSettingsCache(): void {
  cached = null;
}
